import styled from '@emotion/styled';
import {media, spacing, Visible} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {MenuItem} from 'UI/components/modules/Header/MenuItem';

import {MenuBase, MenuTransitions} from 'UI/animations/header';

import {useMenu} from './useMenu';
import type {getLinkData} from 'Application/utils/linkFieldUtils';
import {BurgerMenu} from 'Images/icons';

import type {HeaderNavigation} from 'types/payload-types';

/**
 * The props for the `Menu` component, which renders the main navigation
 * including navigation links and an optional call-to-action (CTA) button.
 * This interface defines the expected structure for navigation data,
 * CTA configuration, and testing attributes.
 */
interface ComponentProps {
    /**
     * The configuration for the call-to-action (CTA) button.
     * Derived from `getLinkData`, it includes the label, href,
     * and additional link attributes like `rel` and `target`.
     */
    ctaData: ReturnType<typeof getLinkData>;
    /**
     * The list of navigation items to display in the menu.
     * Each entry should include an ID and a `navigationLink`.
     */
    navigation: HeaderNavigation;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Menu'
     */
    testId?: string;
}

/**
 * A responsive navigation menu component with toggleable visibility and animated transitions.
 * The `Menu` component renders the main navigation of the application, including
 * a responsive hamburger toggle button for smaller screens and a list of navigation
 * links for larger layouts. It also supports an optional call-to-action (CTA) button
 * rendered as the last menu item.
 * The component uses the `useMenu` hook to manage its open/close state and transitions,
 * which are handled with animation variants (`MenuBase`, `MenuTransitions`).
 * Accessibility attributes (`aria-*`) are applied for improved screen reader support.
 * The component is wrapped with MobX’s `observer` to re-render reactively when observable
 * data changes. A `testId` is applied as a `data-cy` attribute for reliable end-to-end testing.
 *
 * @param props            The component props.
 * @param props.ctaData    The CTA button configuration, including label, link, and target attributes.
 * @param props.navigation The list of navigation items, each containing an ID and a `navigationLink`.
 * @param props.testId     An optional identifier used for testing, applied as `data-cy`. Defaults to `'Menu'`.
 * @returns A JSX element rendering the responsive navigation menu with links and a CTA button.
 *
 * @example
 * ```tsx
 * <Menu
 *   navigation={[
 *     { id: "1", navigationLink: { label: "Home", linkType: "internal", internal: "/" } },
 *     { id: "2", navigationLink: { label: "About", linkType: "internal", internal: "/about" } }
 *   ]}
 *   ctaData={{
 *     label: "Contact Us",
 *     href: "/contact",
 *     rel: undefined,
 *     target: undefined
 *   }}
 * />
 * ```
 */
const Menu = observer(({ctaData, navigation, testId = 'Menu'}: ComponentProps) => {
    const {handleClick, handleToggle, initial, transition} = useMenu();

    return (
        <Wrapper
            animate={transition}
            aria-label="Hauptnavigation"
            data-cy={testId}
            id="menu"
            initial={initial}
            variants={MenuBase}
        >
            <Visible isLoadingHtml lg md sm xs>
                <Button
                    aria-controls="mainMenu"
                    aria-expanded={transition === 'open'}
                    aria-haspopup="true"
                    aria-labelledby="menu"
                    as="button"
                    forceSmall
                    onClick={handleToggle}
                >
                    {/* @ts-expect-error */}
                    <BurgerMenu height={20} width={20} />
                </Button>
            </Visible>
            <MenuList
                aria-labelledby="menu"
                id="mainMenu"
                role="menu"
                variants={MenuTransitions}
            >
                {navigation?.map(link => (
                    <MenuItem key={link.id} link={link.navigationLink} onClick={handleClick} />
                ))}
                <Visible isLoadingHtml lg md sm xs>
                    <MenuItemButton role="none">
                        <Button
                            as="link"
                            href={ctaData.href!}
                            rel={ctaData.rel}
                            role="menuitem"
                            target={ctaData.target}
                            onClick={handleClick}
                        >
                            {ctaData.label}
                        </Button>
                    </MenuItemButton>
                </Visible>
            </MenuList>
        </Wrapper>
    );
});

Menu.displayName = 'Menu';

export {Menu};

const Wrapper = styled(motion.nav)`
    ${media('xl')} {
        backdrop-filter: blur(20px);
        background: hsl(from ${({theme}) => theme.colors.neutral100} h s l / 0.85);
        border: 1px solid hsl(from ${({theme}) => theme.colors.neutral0} h s l / 0.80);
        border-radius: 200px;
        padding: ${spacing(2)} ${spacing(10)};
    }
`;

const MenuList = styled(motion.ul)`
    backdrop-filter: blur(20px);
    background: hsl(from ${({theme}) => theme.colors.neutral100} h s l / 0.85);
    border: 0px solid hsl(from ${({theme}) => theme.colors.neutral0} h s l / 0.80);
    border-radius: 32px;
    height: 0px;
    left: 0;
    list-style: none;
    margin: 0;
    opacity: 0;
    overflow: hidden;
    padding: 0 ${spacing(6)};
    position: absolute;
    right: 0;
    top: calc(100% + 8px);

    ${media('xl')} {
        backdrop-filter: initial;
        background: initial;
        border: initial;
        border-radius: initial;
        display: flex;
        gap: ${spacing(6)};
        height: initial;
        left: initial;
        list-style: none;
        opacity: initial;
        overflow: initial;
        padding: initial;
        position: initial;
        right: initial;
        top: initial;
    }
`;

const MenuItemButton = styled.li`
    display: flex;
    height: fit-content;
    justify-content: center;
    padding-block: ${spacing(5)};
    width: 100%;
`;